import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { LogIn } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { DaatanCredit } from '@/components/DaatanCredit';
import { Icon3D } from '@/components/Icon3D';
import { hebrewError } from '@/lib/errors';
import { dateLabels } from '@/lib/dates';
import { DEFAULT_GENERAL } from '@/lib/useShulSettings';
import { FullPageSpinner } from '@/auth/guards';

export function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = dateLabels(new Date());
  // settings/general needs a signed-in user; the login screen uses the same seeded defaults.
  const { shortName, subtitle } = DEFAULT_GENERAL;

  if (loading) return <FullPageSpinner />;
  if (user) return <Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) { setError('יש למלא שם משתמש וסיסמה'); return; }
    setBusy(true);
    try {
      await signIn(username, password, remember);
      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (err) {
      setError(hebrewError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <Icon3D name="hero-building" size={240} eager className="-mb-2 w-60 max-w-[70vw] drop-shadow-sm" />
          <div className="mt-2 flex items-center gap-2">
            <Icon3D name="logo-mark" size={44} eager />
            <div className="text-start">
              <h1 className="font-display text-2xl leading-tight font-bold text-primary">{shortName}</h1>
              <p className="text-sm text-ink-2">{subtitle}</p>
            </div>
          </div>
          <p className="mt-2 text-sm text-muted">{today.weekday}, {today.hebrew}</p>
        </div>

        <form onSubmit={submit} className="mt-6 rounded-2xl border border-border bg-surface p-6 shadow-[var(--shadow-pop)]" noValidate>
          <div className="flex flex-col gap-4">
            <Field label="שם משתמש" htmlFor="username">
              <Input id="username" dir="ltr" autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)} className="text-start" />
            </Field>
            <Field label="סיסמה" htmlFor="password">
              <Input id="password" type="password" dir="ltr" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="text-start" />
            </Field>
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-[15px] text-ink-2">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} />
              להישאר מחובר במחשב הזה
            </label>
            {error ? <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">{error}</p> : null}
            <Button type="submit" size="lg" loading={busy}><LogIn /> כניסה</Button>
          </div>
        </form>
        <p className="mt-5 text-center text-xs text-muted">שכחתם סיסמה? פנו למנהל המערכת</p>
        <DaatanCredit width={170} className="mt-8" />
      </div>
    </div>
  );
}
